import type { AdSize, ResponsiveReserveHeight } from "@sphtech/web2-ads";

import { AdUnitNames } from "./types";

/**
 * SDK ad-slot holder contract:
 *
 * `@sphtech/web2-ads` renders `className` on the GPT slot div, which is the
 * element vendor scripts and in-flow creatives mutate. Keep legacy holder
 * classes on the GPT holder node and limit wrapper divs to measurement
 * attributes or page-owned layout such as sticky positioning.
 */
export const SDK_AD_SLOT_HOLDER_BASE_CLASS = "ad-placeholder no-print";

/**
 * Per-breakpoint loading-time reservation heights for SDK `<AdSlot>` slots,
 * keyed by viewport `min-width` px (`base` = below the smallest key). The SDK
 * holds this `min-height` (and shows its shimmer skeleton) until the creative
 * paints, then settles to the served size — so the slot reserves space and the
 * page does not jump (CLS), while a subtle loading state covers the wait.
 *
 * These are *balanced* bands tuned to the common fill, deliberately NOT the
 * `sizeMapping`-derived tallest creative: `imu*` carries a `300x600` candidate
 * and `midarticlespecial` a `640x480`, so the SDK's automatic reservation would
 * reserve 600/480px and leave a tall gap for the usual `300x250` fill. Reserving
 * the likely height keeps the skeleton modest; a taller creative settles up
 * inside its page-owned rail (e.g. the `lg:h-[800px]` imu2 column) with no shift
 * to surrounding content. Pass an explicit `reserveHeight` on `<Ad>`/`<AdSlot>`
 * (including `false` to opt out) to override per call site.
 */
export const SDK_AD_SLOT_RESERVE_HEIGHTS: Partial<
  Record<AdUnitNames, ResponsiveReserveHeight>
> = {
  // 320x50/100 band on mobile, up to 970x250 on desktop. Mirrors the legacy
  // min-h-[calc(50px+0.5rem)] lg:min-h-[calc(250px+1.5rem)] holder bands.
  lb1: { base: 58, 1024: 274 },
  lb2: { base: 58, 1024: 274 },
  // Settle on the common 300x250; a desktop 300x600 half-page grows inside its
  // sticky rail rather than reserving 600px of skeleton up front.
  imu1: { base: 250 },
  imu2: { base: 250 },
  imu3: { base: 250 },
  // Native mid-article: balance on 300x250 rather than the 640x480 max.
  midarticlespecial: { base: 250 },
  // Breaking-news list ads: 320x50/100 mobile, 728x90 desktop.
  bn1: { base: 58, 1024: 102 },
  bn2: { base: 58, 1024: 102 },
  bn3: { base: 58, 1024: 102 },
  bn4: { base: 58, 1024: 102 },
};

/**
 * GAM network id for BT — the `/5908` half of the `/5908/bt` ad-unit prefix in
 * `GoogleAdsSlotFactory.prefix`, split into the SDK's `networkId` + `sitePrefix`
 * form. The canary infinite-scroll driver must pass an explicit ad-unit path to
 * `defineInfiniteScrollAds`, because the SDK slot registry (which the
 * declarative `<AdSlot>` reads) is not exported for runtime lookup.
 */
export const BT_GAM_NETWORK_ID = "/5908";

/**
 * Per-article infinite-scroll body-ad sizes in the SDK's `AdSize[]` form, used
 * by the canary article driver (`CanaryInfiniteScrollAdSlot`). These mirror the
 * matching `adSlotSizes` entries below for production parity, but use the SDK's
 * `'fluid'` literal instead of googletag's `['fluid']` wrapper. Only the body
 * units `ParagraphWrapper` repeats per appended article need this — the first
 * article and the listing resolve sizes from the SDK registry via the
 * declarative `<AdSlot>` (counter 0) path.
 */
export const CANARY_INFINITE_SCROLL_AD_SIZES: Partial<
  Record<AdUnitNames, AdSize[]>
> = {
  imu1: ["fluid", [300, 250], [1, 1]],
  imu2: [[300, 250], [300, 600], "fluid", [1, 1]],
  imu3: [[300, 250]],
  midarticlespecial: [
    [1, 1],
    "fluid",
    [640, 480],
    [300, 169],
    [300, 225],
    [300, 250],
    [480, 270],
    [480, 360],
    [640, 36],
  ],
};

export const adSlotSizes: Record<AdUnitNames, googletag.GeneralSize> = {
  imu1: [["fluid"], [300, 250], [1, 1]],
  imu2: [[300, 250], [300, 600], ["fluid"], [1, 1]],
  imu3: [[300, 250]],
  lb1: [
    [970, 90],
    [728, 90],
    [970, 250],
    [320, 50],
    [320, 100],
    [320, 250],
    ["fluid"],
    [1, 1],
  ],
  lb2: [
    [970, 90],
    [728, 90],
    [970, 250],
    [320, 50],
    [320, 100],
    [320, 250],
    ["fluid"],
    [1, 1],
  ],
  prestitial: [1, 1],
  postitial: [1, 1],
  midarticlespecial: [
    [1, 1],
    ["fluid"],
    [640, 480],
    [300, 169],
    [300, 225],
    [300, 250],
    [480, 270],
    [480, 360],
    [640, 36],
  ],
  catfish: [1, 1],
  bn1: [["fluid"], [1, 1], [728, 90], [320, 50], [320, 100]],
  bn2: [["fluid"], [1, 1], [728, 90], [320, 50], [320, 100]],
  bn3: [["fluid"], [1, 1], [728, 90], [320, 50], [320, 100]],
  bn4: [["fluid"], [1, 1], [728, 90], [320, 50], [320, 100]],
};

export const adSizeMappings: Partial<
  Record<AdUnitNames, googletag.SizeMappingArray>
> = {
  lb1: [
    [
      [1024, 0],
      [[970, 90], [728, 90], [970, 250], [1, 1], "fluid"],
    ],
    [
      [740, 0],
      [[728, 90], [1, 1], "fluid"],
    ],
    [
      [320, 0],
      [[320, 50], [320, 100], [320, 250], [1, 1], "fluid"],
    ],
    [
      [0, 0],
      [[320, 50], [320, 100], [320, 250], [1, 1], "fluid"],
    ],
  ],
  lb2: [
    [
      [1024, 0],
      [[970, 90], [728, 90], [970, 250], [1, 1], "fluid"],
    ],
    [
      [740, 0],
      [[728, 90], [1, 1], "fluid"],
    ],
    [
      [320, 0],
      [[320, 50], [320, 100], [320, 250], [1, 1], "fluid"],
    ],
    [
      [0, 0],
      [[320, 50], [320, 100], [320, 250], [1, 1], "fluid"],
    ],
  ],
  bn1: [
    [
      [1024, 0],
      [["fluid"], [1, 1], [728, 90]],
    ],
    [
      [0, 0],
      [["fluid"], [1, 1], [320, 50], [320, 100]],
    ],
  ],
  bn2: [
    [
      [1024, 0],
      [["fluid"], [1, 1], [728, 90]],
    ],
    [
      [0, 0],
      [["fluid"], [1, 1], [320, 50], [320, 100]],
    ],
  ],
  bn3: [
    [
      [1024, 0],
      [["fluid"], [1, 1], [728, 90]],
    ],
    [
      [0, 0],
      [["fluid"], [1, 1], [320, 50], [320, 100]],
    ],
  ],
  bn4: [
    [
      [1024, 0],
      [["fluid"], [1, 1], [728, 90]],
    ],
    [
      [0, 0],
      [["fluid"], [1, 1], [320, 50], [320, 100]],
    ],
  ],
};

export const adPositions: Record<AdUnitNames, string> = {
  bn1: "1",
  bn2: "2",
  bn3: "3",
  bn4: "4",
  catfish: "catfish",
  imu1: "1",
  imu2: "2",
  imu3: "3",
  lb1: "1",
  lb2: "1",
  midarticlespecial: "midarticlespecial",
  prestitial: "prestitial",
  postitial: "postitial",
};

export const adWeights: Record<AdUnitNames, string> = {
  bn1: "1",
  bn2: "2",
  bn3: "3",
  bn4: "4",
  catfish: "3",
  imu1: "5",
  imu2: "6",
  imu3: "7",
  lb1: "1",
  lb2: "1",
  midarticlespecial: "4",
  prestitial: "3",
  postitial: "3",
};

export const SMX_PREBID_JS = import.meta.env.VITE_ADTAG_SMX_PREBID;
